/**
 * Shared derivation of association field subsets from resolved association
 * targets — wraps `decomposeAssociationTargets` plus the lookups both
 * overview-section.tsx (read) and overview-edit-section.tsx (edit) build from
 * it, so the read surface and the edit form agree by construction rather than
 * by two independently-maintained `useMemo` chains.
 */

import { useCallback, useMemo } from 'react';

import type { CrmObject } from '@/types/crm';

import type { DetailField } from '../record/registry/entityTypeRegistry';
import type {
  ResolvedAssociationLike,
  TargetSubsets,
} from '../record/utils/associationTargetSubsets';
import { decomposeAssociationTargets } from '../record/utils/associationTargetSubsets';

const EMPTY_OBJECTS: CrmObject[] = [];
const EMPTY_PRESENT_TARGET_TYPES: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map();
const EMPTY_ALL_TARGETS: ReadonlyMap<string, CrmObject[]> = new Map();

export interface AssociationFieldSubsetsOptions {
  /**
   * Also compute the read-mode extras derived from `assocSubsets`
   * (`presentTargetTypesByAssoc`, `allTargetsByAssoc`). The edit form only
   * needs `assocSubsets` itself (every resolvable type gets a field
   * regardless of what's present), so it can skip this work.
   */
  includeReadExtras?: boolean;
}

export interface AssociationFieldSubsets {
  /** association_type_name -> target-type -> resolved targets of that type. */
  assocSubsets: Map<string, TargetSubsets>;
  /**
   * Which target types this record actually has rows of, per association
   * type. Empty (not computed) unless `includeReadExtras` was passed.
   */
  presentTargetTypesByAssoc: ReadonlyMap<string, ReadonlySet<string>>;
  /**
   * Every target of an association type, flattened across its subsets — for a
   * rule that was NOT decomposed. Empty (not computed) unless
   * `includeReadExtras` was passed.
   */
  allTargetsByAssoc: ReadonlyMap<string, CrmObject[]>;
  /** The resolved targets a field renders — its own subset, or all of them. */
  targetsForField: (field: DetailField) => CrmObject[];
}

export function useAssociationFieldSubsets(
  associations: readonly ResolvedAssociationLike[],
  options: AssociationFieldSubsetsOptions = {}
): AssociationFieldSubsets {
  const { includeReadExtras = false } = options;

  const assocSubsets = useMemo(
    () => decomposeAssociationTargets(associations),
    [associations]
  );

  const presentTargetTypesByAssoc = useMemo(() => {
    if (!includeReadExtras) {
      return EMPTY_PRESENT_TARGET_TYPES;
    }
    const map = new Map<string, ReadonlySet<string>>();
    for (const [assocTypeName, subsets] of assocSubsets) {
      map.set(assocTypeName, new Set(subsets.keys()));
    }
    return map;
  }, [assocSubsets, includeReadExtras]);

  const allTargetsByAssoc = useMemo(() => {
    if (!includeReadExtras) {
      return EMPTY_ALL_TARGETS;
    }
    const map = new Map<string, CrmObject[]>();
    for (const [assocTypeName, subsets] of assocSubsets) {
      map.set(assocTypeName, [...subsets.values()].flat());
    }
    return map;
  }, [assocSubsets, includeReadExtras]);

  const targetsForField = useCallback(
    (field: DetailField): CrmObject[] => {
      if (field.type === 'association' && field.targetEntityTypeName) {
        return (
          assocSubsets.get(field.key)?.get(field.targetEntityTypeName) ??
          EMPTY_OBJECTS
        );
      }
      return allTargetsByAssoc.get(field.key) ?? EMPTY_OBJECTS;
    },
    [assocSubsets, allTargetsByAssoc]
  );

  return {
    assocSubsets,
    presentTargetTypesByAssoc,
    allTargetsByAssoc,
    targetsForField,
  };
}
